import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Table,
  Card,
  InputGroup
} from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import { ReceiveBill } from '../../../components/atoms/StatusLabel'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalSelectCreatePrMethod from '../../../components/modal/pr/ModalSelectCreatePrMethod'
import api from '../../../config/AxiosInstance'
import { PURCHASE_ORDERS } from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import toast, { Toaster } from 'react-hot-toast'
import { Copy, Search, RotateCcw, Plus } from 'lucide-react'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const PurchaseOrder = () => {
  const { state } = useLocation()
  const [loading, setLoading] = useState(false)
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [status, setStatus] = useState(state?.status || '')
  const [po, setPo] = useState(state?.po || '')
  const [debouncedPo, setDebouncedPo] = useState(po)
  const [selectedPrinciple, setSelectedPrinciple] = useState(
    state?.principle || ''
  )
  const [selectedWarehouse, setSelectedWarehouse] = useState(
    state?.warehouse_id || ''
  )

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const { per_page, current_page } = meta
  const [poName, setPoName] = useState(0)
  const [principles, setPrinciples] = useState([])
  const [warehouses, setWarehouses] = useState([])

  const refSrcPrinciples = useRef()
  const refSrcWarehouses = useRef()

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPo(po)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [po])

  useEffect(() => {
    fetchPO(
      per_page,
      current_page,
      selectedPrinciple,
      selectedWarehouse,
      debouncedPo,
      status
    )
  }, [
    per_page,
    current_page,
    debouncedPo,
    selectedPrinciple,
    selectedWarehouse,
    status
  ])

  useEffect(() => {
    getPrinciples()
    getWarehouses()
  }, [])

  const getPrinciples = async () => {
    try {
      const options = [{ value: '', label: 'All' }]
      const res = await api.get('/inbound/purchase-order/principles')
      const { data } = res.data
      data.map((data) =>
        options.push({ value: data.supplier, label: data.supplier })
      )
      setPrinciples(options)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  const getWarehouses = async () => {
    try {
      const options = [{ value: '', label: 'All' }]
      const res = await api.get('/inbound/purchase-order/dest-warehouses')
      const { data } = res.data
      data.map((data) =>
        options.push({ value: data.set_warehouse, label: data.set_warehouse })
      )
      setWarehouses(options)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: parseInt(data.selected + 1)
    }))
    window.scrollTo(0, 0)
  }

  async function fetchPO(
    per_page,
    current_page,
    selectedPrinciple,
    selectedWarehouse,
    po,
    status
  ) {
    try {
      setLoading(true)
      const res = await api.get('inbound/purchase-order', {
        params: {
          page: current_page,
          per_page,
          principle: selectedPrinciple || '',
          dest_warehouse: selectedWarehouse || '',
          po: po || '',
          status: status || ''
        }
      })
      const { metadata, data } = res.data

      setPurchaseOrders(data)
      setMeta(metadata)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const navigate = useNavigate()
  const [modalSelectMethodShow, setModalSelectMethodShow] = useState(false)
  const [dataCreatePR, setDataCreatePR] = useState({})

  function handleCreate(code, data) {
    setModalSelectMethodShow(true)
    setPoName(code)
    setDataCreatePR(data)
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!', {
      position: 'top-right',
      style: {
        fontSize: '14px',
        fontWeight: '600'
      }
    })
  }

  const navigateToDetail = (purchaseOrder) => {
    const encodedName = encodeURIComponent(purchaseOrder.name)
    navigate(`/purchase-order/detail/${encodedName}`, {
      state: {
        warehouse_id: selectedWarehouse,
        principle: selectedPrinciple,
        po: po,
        status: status
      }
    })
  }

  function handleReset() {
    refSrcPrinciples.current?.clearValue()
    refSrcWarehouses.current?.clearValue()
    setSelectedPrinciple('')
    setSelectedWarehouse('')
    setPo('')
    setDebouncedPo('')
    setStatus('')
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: 1
    }))
  }

  const SelectStyles = {
    control: (base, state) => ({
      ...base,
      height: 45,
      minHeight: 45,
      borderRadius: '10px',
      borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
      boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
      backgroundColor: '#ffffff',
      border: '1px solid #e9ecef',
      fontSize: '0.9rem'
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      height: '45px',
      padding: '0 12px'
    }),
    input: (provided, state) => ({
      ...provided,
      margin: '0px'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#495057'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'var(--primary-orange)'
        : state.isFocused
          ? '#fff5e6'
          : null,
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer'
    })
  }

  const FormControlStyle = {
    height: '45px',
    borderRadius: '10px',
    border: '1px solid #e9ecef',
    backgroundColor: '#ffffff',
    fontSize: '0.9rem'
  }

  return (
    <>
      <Container fluid className="px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Purchase Order</h3>
          </Col>
          <Col className="text-end">
            <Button
              variant="light"
              className="text-secondary fw-bold rounded px-3 border"
              onClick={() => handleReset()}
              style={{ fontSize: '0.85rem' }}
            >
              <RotateCcw size={16} className="me-2" />
              Reset Filters
            </Button>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>Principle</Form.Label>
                <Select
                  isClearable
                  onChange={(e) => {
                    setSelectedPrinciple(e?.value || '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  options={principles}
                  value={
                    selectedPrinciple !== ''
                      ? principles?.find(
                          (principle) => principle.value === selectedPrinciple
                        )
                      : ''
                  }
                  isSearchable
                  ref={refSrcPrinciples}
                  styles={SelectStyles}
                  placeholder="All Principles"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>Dest. Warehouse</Form.Label>
                <Select
                  isClearable
                  value={
                    selectedWarehouse !== ''
                      ? warehouses?.find(
                          (warehouse) => warehouse.value === selectedWarehouse
                        )
                      : ''
                  }
                  onChange={(e) => {
                    setSelectedWarehouse(e?.value || '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  options={warehouses}
                  isSearchable
                  ref={refSrcWarehouses}
                  styles={SelectStyles}
                  placeholder="All Warehouses"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>PO Number</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRight: 'none',
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px'
                    }}
                  >
                    <Search size={18} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={po}
                    onChange={(e) => setPo(e.target.value)}
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                    placeholder="Search by PO Number..."
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>Status</Form.Label>
                <Form.Select
                  aria-label="Select Status"
                  name="status"
                  id="status"
                  onChange={(e) => {
                    setStatus(e.target.value)
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  value={status}
                  style={FormControlStyle}
                >
                  <option value="">All Statuses</option>
                  {PURCHASE_ORDERS.map((devType, index) => (
                    <option value={devType.value} key={devType.id}>
                      {devType.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <Row>
          <Col md={12}>
            <Table
              hover
              responsive
              className="shadow-sm mb-5 bg-white"
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                fontSize: '0.9rem'
              }}
            >
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    #PO
                  </th>
                  <th
                    className="p-2 text-end align-middle"
                    style={tableHeaderStyle}
                  >
                    Principle
                  </th>
                  <th
                    className="p-2 text-end align-middle"
                    style={{ ...tableHeaderStyle, width: '25%' }}
                  >
                    Dest. Warehouse
                  </th>
                  <th
                    className="p-2 text-end align-middle"
                    style={tableHeaderStyle}
                  >
                    Status
                  </th>
                  <th
                    className="p-2 text-end align-middle"
                    style={tableHeaderStyle}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5}>
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && purchaseOrders.length === 0 && (
                  <RowEmpty colSpan={5} />
                )}
                {!loading &&
                  purchaseOrders?.map((purchaseOrder, i) => {
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td className="p-2 align-middle fw-semibold text-primary">
                          <div className="d-flex align-items-center gap-2">
                            {purchaseOrder.name}
                            <Copy
                              size={14}
                              className="text-muted cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                opacity: 0.5,
                                transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = 1)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = 0.5)
                              }
                              onClick={() => handleCopy(purchaseOrder.name)}
                              title="Copy PO Number"
                            />
                          </div>
                        </td>
                        <td
                          className="p-2 text-end align-middle text-muted"
                          style={{ width: '25%' }}
                        >
                          {purchaseOrder.supplier}
                        </td>
                        <td className="p-2 text-end align-middle text-muted">
                          {purchaseOrder.set_warehouse}
                        </td>
                        <td className="p-2 text-end align-middle">
                          <ReceiveBill text={purchaseOrder.status} />
                        </td>
                        <td className="p-2 text-end align-middle">
                          <TableAction>
                            <button
                              type="button"
                              className="btn btn-light text-secondary border rounded"
                              style={{
                                width: '32px',
                                height: '32px',
                                padding: 0,
                                marginRight: '8px'
                              }}
                              onClick={() => {
                                navigateToDetail(purchaseOrder)
                              }}
                              title="View Detail"
                            >
                              <Search size={16} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary-blue btn-sm rounded px-3"
                              onClick={() =>
                                handleCreate(purchaseOrder.name, purchaseOrder)
                              }
                            >
                              <small className="fw-bold d-flex align-items-center gap-1">
                                <Plus size={14} strokeWidth={3} />
                                PR DRAFT
                              </small>
                            </button>
                          </TableAction>
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="p-3 border-top-0">
                    <TablePagination
                      meta={meta}
                      handleOnChangeMeta={handleOnChangeMeta}
                      handlePageChange={handlePageChange}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Col>
        </Row>
      </Container>

      <ModalSelectCreatePrMethod
        show={modalSelectMethodShow}
        onHide={() => setModalSelectMethodShow(false)}
        code={poName}
        data={dataCreatePR}
      />

      <Toaster />
    </>
  )
}

export default PurchaseOrder
